import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, gql } from "@apollo/client";
import { FileText } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { format } from "date-fns";
import { toast } from "react-toastify"; // Ensure toast is imported if used

const ADD_NOTE = gql`
  mutation AddNote($odrnumber: Int!, $program: String!, $input: ParticipantNoteInput) {
    createProgramNote(odrnumber: $odrnumber, program: $program, input: $input) {
      message
    }
  }
`;

interface GeneralNoteEntryFormProps {
  odrNumber: string;
  participantId: string; // This will be the program passed to the mutation
  user: {
    realname: string; // Assuming user.realname exists
    name?: string;
    username?: string;
  };
  showHeader?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export const GeneralNoteEntryForm: React.FC<GeneralNoteEntryFormProps> = ({
  odrNumber,
  participantId,
  user,
  showHeader = true,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    note: "",
    notesBy: user.realname || user.name || user.username || "", // Use realname first
    notesDate: format(new Date(), "yyyy-MM-dd"),
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const [addNote, { loading }] = useMutation(ADD_NOTE);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    if (!formData.note.trim()) {
      newErrors.note = "Note cannot be empty.";
    }
    if (!formData.notesBy.trim()) {
      newErrors.notesBy = "Entered By cannot be empty.";
    }
    if (!formData.notesDate.trim()) {
      newErrors.notesDate = "Note Date cannot be empty.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await addNote({
        variables: {
          odrnumber: parseInt(odrNumber), // Mutation expects Int!
          program: participantId,         // program is String!
          input: {
            note: formData.note,
            noteBy: formData.notesBy,
            noteDate: new Date(formData.notesDate).toISOString()
          }
        }
      });
      toast.success("General Note added successfully");
      onSuccess();
    } catch (error) {
      console.error("Error adding General Note:", error);
      toast.error(`Error adding General Note: ${error}`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {showHeader && (
        <SectionHeader
          icon={FileText}
          title="General Note"
          subtitle="Add a program note"
        />
      )}
      
      <div className="space-y-2">
        <Label htmlFor="notesBy">Entered By</Label>
        <Input
          id="notesBy"
          name="notesBy"
          value={formData.notesBy}
          onChange={handleInputChange}
          required
          className={`bg-gray-100 ${errors.notesBy ? "border-red-500" : ""}`}
          readOnly
        />
        {errors.notesBy && <p className="text-sm text-red-500">{errors.notesBy}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="note">Note</Label>
        <Textarea
          id="note"
          name="note"
          value={formData.note}
          onChange={handleInputChange}
          placeholder="Enter your note here"
          className={`min-h-[150px] ${errors.note ? "border-red-500" : ""}`}
          required
        />
        {errors.note && <p className="text-sm text-red-500">{errors.note}</p>}
      </div>

      <div className="flex items-end justify-between space-x-2 mt-6 pt-4 border-t">
        <div className="space-y-2 w-1/3">
            <Label htmlFor="notesDate">Note Date</Label>
            <Input
            type="date"
            id="notesDate"
            name="notesDate"
            value={formData.notesDate}
            onChange={handleInputChange}
            required
            className={errors.notesDate ? "border-red-500" : ""}
            />
            {errors.notesDate && <p className="text-sm text-red-500">{errors.notesDate}</p>}
        </div>

        <div className="flex space-x-2">
            <Button size="sm" type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Note"}
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel} type="button">
            Cancel
            </Button>
        </div>
      </div>
    </form>
  );
};
